import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from '../utils/safeToast';
import {
  Home,
  ClipboardList,
  AlertTriangle,
  CheckCircle,
  Clock,
  Plus,
  ArrowRight,
  Monitor,
  Info
} from 'lucide-react';

const API = "/api";

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [versionInfo, setVersionInfo] = useState(null);

  useEffect(() => {
    fetchData();
    fetchVersionInfo();
  }, []);

  const fetchVersionInfo = async () => {
    try {
      const response = await fetch('/version.json?' + Date.now());
      if (response.ok) {
        const data = await response.json();
        setVersionInfo(data);
      }
    } catch (error) {
      setVersionInfo({ version: 'unbekannt', buildDate: null });
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, tasksRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/tasks`)
      ]);
      setStats(statsRes.data);
      setRecentTasks(tasksRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      neu: 'bg-blue-100 text-blue-700',
      in_bearbeitung: 'bg-amber-100 text-amber-700',
      erledigt: 'bg-emerald-100 text-emerald-700'
    };
    const labels = {
      neu: 'Neu',
      in_bearbeitung: 'In Bearbeitung',
      erledigt: 'Erledigt'
    };
    return (
      <Badge className={`${styles[status]} border-0`} data-testid={`status-badge-${status}`}>
        {labels[status]}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    if (priority === 'hoch') {
      return (
        <Badge className="bg-[#0E4C92] text-white border-0 font-bold" data-testid="priority-badge-hoch">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Hoch
        </Badge>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0E4C92]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Übersicht Ihrer Ferienwohnungen</p>
        </div>
        <div className="flex gap-3">
          <Link to="/monitoring">
            <Button variant="outline" className="rounded-lg border-[#0E4C92] text-[#0E4C92]" data-testid="goto-monitoring-btn">
              <Monitor className="w-4 h-4 mr-2" />
              Monitoring
            </Button>
          </Link>
          <Link to="/tasks">
            <Button className="bg-[#0E4C92] hover:bg-[#0A3A70] text-white rounded-lg" data-testid="new-task-btn">
              <Plus className="w-4 h-4 mr-2" />
              Neue Aufgabe
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all" data-testid="stat-properties">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Objekte</p>
                <p className="text-3xl font-bold text-slate-900 font-['Manrope']">
                  {stats?.total_properties || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                <Home className="w-6 h-6 text-[#0E4C92]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all" data-testid="stat-tasks-new">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Neue Aufgaben</p>
                <p className="text-3xl font-bold text-blue-600 font-['Manrope']">
                  {stats?.tasks_new || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <ClipboardList className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all" data-testid="stat-tasks-progress">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">In Bearbeitung</p>
                <p className="text-3xl font-bold text-amber-600 font-['Manrope']">
                  {stats?.tasks_in_progress || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-all" data-testid="stat-high-priority">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Hohe Priorität</p>
                <p className="text-3xl font-bold text-[#0E4C92] font-['Manrope']">
                  {stats?.tasks_high_priority || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-[#0E4C92]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Tasks */}
      <Card className="bg-white border border-slate-200 shadow-sm" data-testid="recent-tasks">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-['Manrope']">Aktuelle Aufgaben</CardTitle>
          <Link to="/tasks">
            <Button variant="ghost" className="text-[#0E4C92] hover:text-[#0A3A70]">
              Alle anzeigen
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {recentTasks.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
              <p className="text-slate-500">Keine offenen Aufgaben</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentTasks.map((task) => (
                <Link
                  key={task.id}
                  to={`/tasks/${task.id}`}
                  className={`block p-4 rounded-xl border transition-all bg-white ${
                    task.priority === 'hoch'
                      ? 'border-l-4 border-l-[#0E4C92] border-slate-200 hover:shadow-md'
                      : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                  }`}
                  data-testid={`task-item-${task.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-slate-900 truncate">{task.title}</h3>
                        {getPriorityBadge(task.priority)}
                      </div>
                      <p className="text-sm text-slate-500 truncate">{task.property_name}</p>
                    </div>
                    {getStatusBadge(task.status)}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-slate-900 font-['Manrope']">Aufgaben gesamt</h3>
              <span className="text-2xl font-bold text-slate-900">{stats?.total_tasks || 0}</span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Erledigt</span>
                <span className="font-medium text-emerald-600">{stats?.tasks_completed || 0}</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div
                  className="bg-emerald-500 h-2 rounded-full transition-all"
                  style={{
                    width: `${stats?.total_tasks ? (stats.tasks_completed / stats.total_tasks) * 100 : 0}%`
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#0E4C92] text-white shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-100 mb-1">Schnellzugriff</p>
                <h3 className="text-lg font-medium font-['Manrope']">Neues Objekt anlegen</h3>
              </div>
              <Link to="/properties">
                <Button className="bg-white text-[#0E4C92] hover:bg-blue-50 rounded-lg">
                  <Plus className="w-4 h-4 mr-2" />
                  Anlegen
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Version Info Footer */}
      {versionInfo && (
        <div className="flex items-center justify-center gap-2 text-xs text-slate-400 pt-4" data-testid="version-info">
          <Info className="w-3 h-3" />
          <span>
            Version {versionInfo.baseVersion || versionInfo.version?.split('.').slice(0, 2).join('.') || 'unbekannt'}.0
            {versionInfo.buildDate && (
              <> · {new Date(versionInfo.buildDate).toLocaleString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
              })}</>
            )}
          </span>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
